import React, { useRef, useState } from "react";
import "./styles.scss";
import {
  ActionLinks,
  CarBasicInfo,
  IActionLink,
  Icon,
  IconName,
  MyTripsCard,
  TripInfoDetails,
  useDeviceTypes,
} from "halifax";
import {
  CarReservation,
  DistKindEnum,
  IOpenModal,
  ItineraryEnum,
  ItineraryWithType,
  MyTripsFilter,
  MyTripsModalTypes,
  Reservation,
  VIEWED_ITEMIZED_RECEIPT,
  ItemizedReceiptEventsProperties,
} from "redmond";
import clsx from "clsx";
import { Box, Popover, Typography } from "@material-ui/core";
import {
  shouldAddOrSimilar,
  normalizeVehicleName,
  getCarDetailsText,
} from "@capone/common";
import { useExperimentIsVariant } from "@capone/experiments";

import { CarInfoContent } from "../CarInfoContent";
import { CarMap } from "../CarMap";
import { getTotalBaggageQty, getCancellationPolicy } from "../../helpers";
import { ConfirmationSummary } from "../../../ConfirmationSummary";
import { getLabels, getValues } from "../../../FlightCard/helpers";
import { PaymentSummary } from "../../../PaymentSummary";
import { SummaryCard } from "../../../SummaryCard";
import { TravelersSummary } from "../../../TravelersSummary";
import { TripDescriptionSummary } from "../../../TripDescriptionSummary";
import * as textConstants from "../../constants";
import {
  DESKTOP_OFFSET_SCROLL,
  HOW_REFUNDS_WORK_LINK,
  HOW_REFUNDS_WORK_TEXT,
  MOBILE_OFFSET_SCROLL,
} from "../../../../constants";
import { getCurrencyString } from "../../../../../../../../utils/helpers";
import { trackEvent } from "../../../../../../../../api/v1/analytics/trackEvent";

interface IDesktopCarCardProps {
  expandedCard: string;
  car: CarReservation;
  banner?: JSX.Element;
  isCancel?: boolean;
  isPastTrips: boolean;
  confirmationCodeClassName?: string;
  isMobile?: boolean;
  onExpandCard: (cardId: string) => void;
  setOpenModal: (modalType: IOpenModal) => void;
  driverName?: string[];
  tripsFilter: MyTripsFilter;
}

export const addCarType = (car: CarReservation): ItineraryWithType => ({
  ...car,
  type: ItineraryEnum.Car,
});

export const getPostalCode = (reservation: Reservation): string => {
  const { lodgingData } = reservation;
  const city = lodgingData?.city ? `${lodgingData.city},` : "";
  const state = lodgingData?.state?.code ? `${lodgingData.state.code},` : "";
  const country = lodgingData?.country ? `${lodgingData.country}` : "";
  return `${city} ${state} ${country}`;
};

export const DesktopCarCard = (props: IDesktopCarCardProps) => {
  const {
    expandedCard,
    car,
    isMobile,
    isPastTrips,
    onExpandCard,
    setOpenModal,
    banner,
    isCancel,
    confirmationCodeClassName,
    driverName,
    tripsFilter,
  } = props;
  const { bookResult, paymentAmountInfo, tripDescription } = car;
  const { rentalRate, vehicle } = bookResult;
  const { passengerQty } = vehicle;
  const bags = getTotalBaggageQty(vehicle);
  const onOpenModal = (type: MyTripsModalTypes) =>
    setOpenModal({
      type,
      selectedItinerary: addCarType(car),
    });
  const { matchesMobile } = useDeviceTypes();
  const isExpenseManagementEnabled = useExperimentIsVariant(
    "corp-expense-management",
    "available"
  );
  const refundPopoverRef = useRef<HTMLButtonElement>(null);
  const [refundPopoverOpen, setRefundPopoverOpen] = useState(false);

  // TODO replace with real actions when self-serve is ready
  const actions: IActionLink[] = [];
  const showCancelButton = !isPastTrips;

  const closeRefundPopover = () => setRefundPopoverOpen(false);

  const openRefundPopover = () => setRefundPopoverOpen(true);

  if (!isCancel && showCancelButton) {
    actions.push(
      {
        content: "Change car rental",
        onClick: () => onOpenModal(MyTripsModalTypes.ChangeCar),
      },
      {
        content: "Cancel car rental",
        onClick: () => onOpenModal(MyTripsModalTypes.CancelCar),
      }
    );
  } else if (isCancel) {
    actions.push({
      content: (
        <>
          {HOW_REFUNDS_WORK_LINK}
          <Icon className="info-icon" name={IconName.InfoCircle} />
        </>
      ),
      linkClassName: "how-refunds-work-link",
      linkRef: refundPopoverRef,
      onClick: openRefundPopover,
    });
  }

  actions.push({
    content: "Resend confirmation",
    onClick: () => onOpenModal(MyTripsModalTypes.ResendConfirmation),
  });

  if (!isCancel) {
    actions.push({
      content: "View itemized receipt",
      onClick: () => {
        onOpenModal(MyTripsModalTypes.ItemizedCarReceipt);
        trackEvent({
          eventName: VIEWED_ITEMIZED_RECEIPT,
          properties: {
            trip_id: bookResult.groundBookingId,
            travel_type: "car",
            active_tab: tripsFilter,
          } satisfies ItemizedReceiptEventsProperties,
        });
      },
    });
  }

  return (
    <Box
      id={bookResult.groundBookingId}
      key={bookResult.groundBookingId}
      className={clsx("car-trip-container", {
        mobile: isMobile,
        expanded: expandedCard === bookResult.groundBookingId,
      })}
    >
      <MyTripsCard
        className="trip-card-title"
        banner={banner}
        content={
          <TripInfoDetails
            hideTitleTag={isCancel}
            confirmationCodeClassName={confirmationCodeClassName}
            titles={{
              confirmationLabel: textConstants.CONFIRMATION,
              startLabel: `${textConstants.PICK_UP}:`,
              endLabel: `${textConstants.DROP_OFF}:`,
              streetAddress: bookResult.supplier.name,
              postalCode: bookResult.pickUp.location.name,
              phoneNum: bookResult.pickUp.location.address,
              subtitle: [
                normalizeVehicleName(bookResult.vehicle.name),
                shouldAddOrSimilar(bookResult.vehicle.name) ? "or similar" : "",
              ]
                .filter(Boolean)
                .join(" "),
              ...textConstants.getCarInfoDetails(car),
            }}
          />
        }
        actions={<ActionLinks actions={actions} />}
        expandString={
          expandedCard === bookResult.groundBookingId
            ? textConstants.VIEW_LESS
            : textConstants.VIEW_DETAILS
        }
        expandIcon={
          expandedCard === bookResult.groundBookingId
            ? IconName.MinusBlueCircle
            : IconName.PlusBlueCircle
        }
        isExpanded={expandedCard === bookResult.groundBookingId}
        topRightAction={
          bookResult.supplier.logo ? (
            <img
              className="supplier-icon"
              src={bookResult.supplier.logo}
              alt="Supplier logo"
            />
          ) : undefined
        }
        onExpandClick={() => {
          onExpandCard(bookResult.groundBookingId);
          setTimeout(() => {
            const OFFSET = matchesMobile
              ? MOBILE_OFFSET_SCROLL
              : DESKTOP_OFFSET_SCROLL;
            const cardTop =
              document
                ?.getElementById(bookResult.groundBookingId)
                ?.getBoundingClientRect().top || 0;
            window.scrollBy({
              top: (cardTop as number) - OFFSET,
              behavior: matchesMobile ? "auto" : "smooth",
            });
          }, 100);
        }}
        travelers={driverName}
      />

      {bookResult.groundBookingId === expandedCard && (
        <Box className="car-desktop-expanded-container">
          <Box className="car-summary-container">
            <CarMap car={car} />
            <Box className="car-info-container">
              <CarBasicInfo
                carModelDetails={{
                  carSize: vehicle.label,
                  // TODO: ask about where we get multi-car entries
                  carModels: [normalizeVehicleName(vehicle.name)],
                }}
                carDetailsText={getCarDetailsText(vehicle.name)}
                carInfoIconDetails={{
                  passengerCapacity: passengerQty
                    ? `${passengerQty} ${
                        passengerQty > 1 ? "Passengers" : "Passenger"
                      }`
                    : "0 Passengers",
                  bagCapacity:
                    bags != null && bags > 0
                      ? `${bags} ${bags === 1 ? "Bag" : "Bags"}`
                      : undefined,
                  transmissionType: vehicle.transmissionType,
                  airConditioning: vehicle.airConditioning
                    ? "Air conditioning"
                    : undefined,
                }}
                carAvailabilityTagDetails={{
                  freeCancellation: rentalRate.extensions.cancellation.free,
                  CancellationPolicy: getCancellationPolicy(bookResult),
                  hasUnlimitedMileage:
                    rentalRate.extensions.distance.DistKind ===
                    DistKindEnum.Unlimited,
                }}
                topRightElement={<Box />}
                isMobile={isMobile}
                carSizeTypographyVariant="h4"
              />
              <CarInfoContent car={car} />
            </Box>
          </Box>
          <Box
            className={clsx("summary-info-container", {
              "with-trip-description":
                isExpenseManagementEnabled && Boolean(tripDescription),
            })}
          >
            <SummaryCard
              className="travellers-summary"
              action={
                <Typography
                  variant="body2"
                  className="details-link"
                  onClick={() => onOpenModal(MyTripsModalTypes.DriversModal)}
                >
                  Details
                </Typography>
              }
            >
              <TravelersSummary
                label={textConstants.DRIVER_INFO}
                travelers={`${bookResult.driver?.givenName} ${bookResult.driver?.surname}`}
                loyaltyInfo={bookResult.confirmationInfo?.loyaltyInfo}
              />
            </SummaryCard>
            <SummaryCard
              className="payment-summary"
              action={
                <Typography
                  variant="body2"
                  className="details-link"
                  onClick={() => onOpenModal(MyTripsModalTypes.PaymentModal)}
                >
                  Details
                </Typography>
              }
            >
              <PaymentSummary
                tripTotalAmount={getCurrencyString(rentalRate.payNow.fiat)}
                cardLabel={getLabels(paymentAmountInfo).cardLabel}
                cardAmount={getValues(paymentAmountInfo).cardValue}
                rewardsLabel={getLabels(paymentAmountInfo).rewardLabel}
                rewardsAmount={getValues(paymentAmountInfo).rewardValue}
                showCardLabel={Boolean(getLabels(paymentAmountInfo).cardLabel)}
                showRewardsLabel={Boolean(
                  getLabels(paymentAmountInfo).rewardLabel
                )}
              />
            </SummaryCard>
            <SummaryCard
              className="confirmation-summary"
              // action={
              //   <Typography
              //     variant="body2"
              //     className="details-link"
              //     onClick={() =>
              //       onOpenModal(MyTripsModalTypes.ConfirmationModal)
              //     }
              //   >
              //     Details
              //   </Typography>
              // }
            >
              <ConfirmationSummary
                confirmationCode={`H-${
                  bookResult.confirmationInfo?.supplierBookingId ||
                  bookResult.confirmationInfo?.providerBookingId ||
                  bookResult.groundBookingId
                }`}
              />
            </SummaryCard>
            {isExpenseManagementEnabled && tripDescription && (
              <SummaryCard className="trip-description-summary">
                <TripDescriptionSummary tripDescription={tripDescription} />
              </SummaryCard>
            )}
          </Box>
        </Box>
      )}
      {isCancel && (
        <Popover
          anchorEl={refundPopoverRef.current}
          anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
          className="how-refunds-work-popover"
          onClose={closeRefundPopover}
          open={refundPopoverOpen}
        >
          <Typography className="subtitle">{HOW_REFUNDS_WORK_TEXT}</Typography>
        </Popover>
      )}
    </Box>
  );
};
